'use client';

import type { Project } from '@/app/models/project';
import type { RuleOverheadProject } from '@/app/models/ruleOverheadProject';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import { useSearchOverHeadProjectRuleQuery } from '@/app/services/ruleOverHeadProjectApi';
import AllocationPriorityTable from '@/components/AllocationPriority/AllocationPriorityTable';
import NewEditPriorityAllocation from '@/components/AllocationPriority/newEditPriorityAllocation';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import TextButton from '@/components/common/TextButton';
import { Box, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function AllocationPriority() {
  const theme = useTheme();
  const [project, setProject] = useState<Project | null>(null);
  const [drawerStatus, setDrawerStatus] = useState(false);
  const [selectedRule, setSelectedRule] = useState<RuleOverheadProject | null>(null);
  const { t } = useTranslation(['allocationPriority', 'common']);

  const { data: projectsFiltered } = useSearchOverHeadProjectRuleQuery({ searchTerm: project?.code || '' });

  const [projectSearch, setProjectSearch] = useState('');
  const { data: projects, isFetching: isLoadingClients } = useSearchProjectsQuery(
    {
      searchTerm: projectSearch,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
      bringClosed: true,
    },
    { skip: projectSearch.length < 1 },
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PageTitle
        title={t('allocation.priority.title')}
        titleLabel={` (${t('allocation.priority.subtitle')})`}
        subtitle={t('allocation.priority.description') as string}
      />

      <Box
        sx={{
          display: 'block',
          flexDirection: 'row',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography component="p" variant="fieldHeader">
          {t('origin.project')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <GenericAutoComplete
            value={project}
            onChange={(value) => {
              setProject(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) => `${option?.code} - ${option?.name}`}
            onSearch={(value) => {
              setProjectSearch(value);
            }}
            options={projectSearch !== '' && projects?.content ? projects?.content : []}
            placeholder={t('enter.project.name.or.code') || ''}
            loading={isLoadingClients}
            sx={{ width: theme.spacing(50) }}
          />
          <TextButton
            sx={{ ml: 'auto' }}
            text={`+ ${t('new.priority')}`}
            onClick={() => {
              setSelectedRule(null);
              setDrawerStatus(true);
            }}
          />
        </Box>
      </Box>

      <AllocationPriorityTable
        rows={projectsFiltered || []}
        setSelectedItem={setSelectedRule}
        setDrawerOpen={setDrawerStatus}
      />

      <LateralDrawer open={drawerStatus} setOpen={setDrawerStatus} position="right" size="bigger">
        <NewEditPriorityAllocation rule={selectedRule || null} setDrawerOpen={setDrawerStatus} />
      </LateralDrawer>
    </Box>
  );
}

export default AllocationPriority;
